/* Cart drawer — single owner of adding, refreshing and opening.
 *
 * WHY THIS EXISTS
 * The old flow had five components writing the same global state. buy-buttons.liquid
 * posted the add, app.js's updateHeaderCart decided when the drawer opened, the drawer's
 * own Alpine component and drawer-modal.js both closed it (with different flags), and
 * four other drawers wrote the same body.overflow. Two of those bugs were structural,
 * not accidental:
 *
 *   1. The drawer opened from the RESPONSE of a second request. Adding meant
 *      /cart/add.js (~0.6s) and then /?view=cart (~0.35s) in series, so the customer
 *      pressed the button and nothing moved for a second.
 *   2. Nothing carried a request identity. Dismissing the drawer before the response
 *      landed meant the response reopened it — and then nothing was left to close it,
 *      so the page stayed locked until a reload.
 *
 * HOW IT WORKS NOW
 * One request. /cart/add.js is asked for the rendered cart-drawer section alongside the
 * add (Shopify's Section Rendering API), so the new contents arrive with the response
 * that created them. The drawer opens on the CLICK, veiled, and the contents drop in
 * when they arrive.
 *
 * Every mutation takes a sequence number. A response whose number is stale is dropped,
 * and a response that arrives after the customer closed the drawer renders quietly
 * without reopening it. Scroll locking is delegated to ScrollLock, which reference
 * counts across every drawer in the theme.
 */
(function () {
  'use strict';

  var SECTION = 'cart-drawer';
  var LOCK_ID = 'cart-drawer';
  var LEAVE_MS = 320;              // must match the CSS leave transition

  var seq = 0;                     // every mutation takes one
  var lastCloseAt = 0;             // when the customer last dismissed the drawer
  var closeTimer = null;
  var lastFocus = null;

  function rootUrl() {
    var r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return r || '/';
  }

  function el() {
    return document.getElementById('CartDrawer');
  }

  function contents() {
    return document.getElementById('CartDrawerContents');
  }

  function isOpen() {
    var d = el();
    return !!d && d.classList.contains('is-active');
  }

  /* ---------------------------------------------------------------- open / close -- */

  function open(options) {
    var d = el();
    if (!d) return;
    if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }

    if (options && options.busy) d.classList.add('is-busy');

    if (!d.classList.contains('is-active')) {
      lastFocus = document.activeElement;
      d.classList.add('is-active');
      // one frame between display and transform, or the slide has nothing to animate from
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { d.classList.add('is-open'); });
      });
      var closeButton = d.querySelector('.close-modal');
      if (closeButton) setTimeout(function () { closeButton.focus({ preventScroll: true }); }, 60);
    }

    // Outside the branch on purpose: holders are a Set, so asking twice costs nothing,
    // and an already-open drawer whose lock was reconciled away would otherwise never
    // get it back.
    if (window.ScrollLock) window.ScrollLock.acquire(LOCK_ID);
  }

  function close(fromCustomer) {
    var d = el();
    // Release first and unconditionally. A drawer that is already hidden but still
    // holding the lock is exactly the state that used to freeze the page.
    if (window.ScrollLock) window.ScrollLock.release(LOCK_ID);
    if (!d || !d.classList.contains('is-active')) return;

    // Remembered so a response still in flight cannot reopen what was just dismissed.
    if (fromCustomer !== false) lastCloseAt = Date.now();

    d.classList.remove('is-open');
    d.classList.remove('is-busy');
    if (window.ScrollLock) window.ScrollLock.release(LOCK_ID);

    closeTimer = setTimeout(function () {
      d.classList.remove('is-active');
      closeTimer = null;
    }, LEAVE_MS);

    if (lastFocus && document.contains(lastFocus)) {
      try { lastFocus.focus({ preventScroll: true }); } catch (e) {}
    }
    lastFocus = null;
  }

  function setBusy(state) {
    var d = el();
    if (d) d.classList.toggle('is-busy', !!state);
  }

  /* ------------------------------------------------------------------ rendering -- */

  function renderSection(html) {
    if (!html) return;
    var host = contents();
    if (!host) return;
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var fresh = parsed.getElementById('CartDrawerContents');
    if (!fresh) return;
    host.innerHTML = fresh.innerHTML;
    announceCount();
  }

  function announceCount() {
    var node = document.querySelector('#CartDrawerContents [data-cart-count]');
    if (!node) return;
    var count = parseInt(node.textContent, 10);
    if (isNaN(count)) return;
    window.dispatchEvent(new CustomEvent('cart-count-updated', { detail: { cartCount: count } }));
  }

  function showError(form, message) {
    var text = (message || '').replace(/:#[a-fA-F0-9]+/g, '');
    if (!text) {
      text = (window.cartStrings && window.cartStrings.error) || 'Something went wrong. Please try again.';
    }
    var box = form && form.querySelector('#product_form-error');
    if (!box) return false;
    var slot = box.querySelector('.cart-item__error-text');
    if (slot) slot.textContent = text;
    box.classList.remove('hidden');
    return true;
  }

  function clearError(form) {
    var box = form && form.querySelector('#product_form-error');
    if (box) box.classList.add('hidden');
  }

  /* ------------------------------------------------------------------ mutations -- */

  function post(url, body, isJson) {
    var headers = { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
    if (isJson) headers['Content-Type'] = 'application/json';
    return fetch(url, { method: 'POST', headers: headers, body: body, credentials: 'same-origin' })
      .then(function (response) {
        return response.text().then(function (text) {
          var data = null;
          try { data = JSON.parse(text); } catch (e) {}
          // A throttled or errored storefront answers with an HTML page, not JSON. That
          // used to blow up in .json() and land in the catch as "something went wrong"
          // with no clue why.
          if (!data) {
            return { ok: false, data: { message: response.status === 429
              ? 'Too many requests, please try again in a moment.'
              : null } };
          }
          return { ok: response.ok && !data.status, data: data };
        });
      });
  }

  function add(form) {
    if (!form) return Promise.resolve({ ok: false });

    var my = ++seq;
    var startedAt = Date.now();
    var drawerWanted = window.cartUISettings && window.cartUISettings.type === 'drawer';

    clearError(form);
    if (drawerWanted) open({ busy: true });
    window.dispatchEvent(new CustomEvent('cart-adding'));

    var body = new FormData(form);
    body.append('sections', SECTION);
    body.append('sections_url', window.location.pathname);

    return post(rootUrl() + 'cart/add.js', body, false)
      .then(function (result) {
        if (my !== seq) return result;               // a newer action has taken over

        if (!result.ok) {
          setBusy(false);
          var data = result.data || {};
          showError(form, data.description || data.message);
          if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: body.get('id'),
              errors: data.errors,
              message: data.message
            });
          }
          // Nothing was added, so a drawer this add opened has no business staying open.
          if (drawerWanted && lastCloseAt < startedAt) close(false);
          return result;
        }

        var sections = result.data.sections || {};
        renderSection(sections[SECTION]);
        setBusy(false);

        if (drawerWanted) {
          if (lastCloseAt > startedAt) {
            // Dismissed while the request was in flight. The cart is updated, the
            // drawer stays shut, and the page stays scrollable — the old code reopened
            // it here and left the customer stuck behind a drawer they had closed.
            if (window.ScrollLock) window.ScrollLock.release(LOCK_ID);
          } else {
            open();
          }
        }

        window.dispatchEvent(new CustomEvent('cart-updated', { detail: false }));
        if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: body.get('id') });
        }
        window.dispatchEvent(new CustomEvent('close-quick-view'));
        return result;
      })
      .catch(function (error) {
        console.error('CartUI.add:', error);
        if (my !== seq) return { ok: false };
        setBusy(false);
        showError(form, null);
        if (drawerWanted && lastCloseAt < startedAt) close(false);
        return { ok: false };
      })
      .then(function (result) {
        window.dispatchEvent(new Event('finish-loading'));
        window.dispatchEvent(new Event('stop-loading'));
        return result;
      });
  }

  function changeLine(line, quantity) {
    var my = ++seq;
    setBusy(true);

    return post(rootUrl() + 'cart/change.js', JSON.stringify({
      line: line,
      quantity: quantity,
      sections: SECTION,
      sections_url: window.location.pathname
    }), true).then(function (result) {
      if (my !== seq) return result;
      setBusy(false);

      if (!result.ok) {
        var message = (result.data && result.data.message) || '';
        var box = document.getElementById('CartDrawer-LineItemError-' + line)
               || document.getElementById('Line-item-error-' + line);
        if (box) {
          var slot = box.querySelector('.cart-item__error-text');
          if (slot) slot.textContent = message.replace(/:#[a-fA-F0-9]+/g, '');
          box.classList.remove('hidden');
        }
        window.dispatchEvent(new Event('stop-loading'));
        return result;
      }

      var sections = (result.data && result.data.sections) || {};
      if (sections[SECTION]) renderSection(sections[SECTION]);
      window.dispatchEvent(new CustomEvent('cart-updated', { detail: false }));
      window.dispatchEvent(new Event('stop-loading'));
      return result;
    }).catch(function (error) {
      console.error('CartUI.changeLine:', error);
      setBusy(false);
      window.dispatchEvent(new Event('stop-loading'));
      return { ok: false };
    });
  }

  /* Refresh without mutating — for callers outside this module (the chat widget adds
   * through its own fetch, the cart page posts its own changes). */
  function refresh(options) {
    var my = ++seq;
    var startedAt = Date.now();
    var wantOpen = !!(options && options.open);
    if (wantOpen) open({ busy: true });

    return fetch(rootUrl() + '?section_id=' + SECTION, { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        if (my !== seq) return;
        renderSection(html);
        setBusy(false);
        if (wantOpen) {
          if (lastCloseAt > startedAt) {
            if (window.ScrollLock) window.ScrollLock.release(LOCK_ID);
          } else {
            open();
          }
        }
        window.dispatchEvent(new Event('stop-loading'));
      })
      .catch(function (error) {
        console.error('CartUI.refresh:', error);
        setBusy(false);
        window.dispatchEvent(new Event('stop-loading'));
      });
  }

  /* ------------------------------------------------------------------- listeners -- */

  // Delegated, so they keep working after the contents are swapped out.
  document.addEventListener('click', function (event) {
    var d = el();
    if (!d || !d.classList.contains('is-active')) return;

    if (event.target.closest('#CartDrawer .close-modal, #CartDrawer [data-cart-close]')) {
      event.preventDefault();
      close();
      return;
    }
    if (event.target.closest('#CartDrawer .overlay')) {
      event.preventDefault();
      close();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape' && event.code !== 'Escape') return;
    if (isOpen()) close();
  });

  // Tab must not walk out of an open drawer and into the page behind it.
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Tab' || !isOpen()) return;
    var d = el();
    var focusable = d.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])');
    var visible = [];
    for (var i = 0; i < focusable.length; i++) {
      if (focusable[i].getBoundingClientRect().width > 0) visible.push(focusable[i]);
    }
    if (!visible.length) return;
    var first = visible[0];
    var last = visible[visible.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  // Compatibility with everything that already speaks these events.
  window.addEventListener('open-cart', function () { open(); });
  window.addEventListener('close-cart', function () { close(); });
  window.addEventListener('cart-busy', function () { setBusy(true); });
  window.addEventListener('cart-ready', function () { setBusy(false); });

  window.CartUI = {
    open: open,
    close: close,
    isOpen: isOpen,
    add: add,
    changeLine: changeLine,
    refresh: refresh,
    setBusy: setBusy
  };
})();
